import React, { ComponentType, Dispatch, memo, SetStateAction, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

export interface ValidationError {
  key?: React.Key;
  message?: React.ReactNode;
  FieldId?: string;
  Message?: string;
  DangerousHtmlMessage?: string;
  [propertyName: string]: unknown;
}

export interface ValidationErrorContextValue {
  validationErrors: ValidationError[];
  setValidationErrors: Dispatch<SetStateAction<ValidationError[]>>;
}

export const ValidationErrorContext = React.createContext<ValidationErrorContextValue>({} as ValidationErrorContextValue);

/**
 * Wraps an element with AlertContext, providing the ability to either read app-level error codes or
 * set an alert with new error codes.
 * @param {Component} WrappedComponent - Component to wrap.
 * @returns {Component} Component with AlertContext.
 */
export const withValidationErrorProvider = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const HoC = memo((props: P) => {
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

    return (
      <ValidationErrorContext.Provider value={{ validationErrors, setValidationErrors }}>
        <WrappedComponent {...props} />
      </ValidationErrorContext.Provider>);
  });

  HoC.displayName = `withValidationErrorProvider(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(HoC, WrappedComponent);
};
